import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Paperclip, FileText, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

const MAX_FILES = 5;
export const ATTACH_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf";

export const OrderAttachments = ({ order, onChanged }) => {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const atts = order.attachments || [];

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File too large — 10MB max"); if (fileRef.current) fileRef.current.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/orders/${order.id}/attachments`, fd);
      toast.success("File attached");
      onChanged?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't attach that file"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const view = async (a) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${a.url}`, { headers: { Authorization: `Bearer ${localStorage.getItem("kc_token")}` } });
      if (!res.ok) throw new Error();
      const obj = URL.createObjectURL(await res.blob());
      window.open(obj, "_blank");
      setTimeout(() => URL.revokeObjectURL(obj), 60000);
    } catch { toast.error("Couldn't open that file"); }
  };

  const remove = async (a) => {
    try { await api.delete(`/orders/${order.id}/attachments/${a.id}`); toast.success("Attachment removed"); onChanged?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't remove it"); }
  };

  return (
    <div data-testid="order-attachments-section">
      {atts.length === 0 && (
        <p className="text-sm text-muted-foreground">No files attached — add a label, invoice or paperwork (JPG/PNG/HEIC/PDF, max 10MB, up to {MAX_FILES}).</p>
      )}
      <div className="space-y-2">
        {atts.map((a) => (
          <div key={a.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm" data-testid={`attachment-row-${a.id}`}>
            {a.content_type === "application/pdf"
              ? <FileText className="h-4 w-4 shrink-0 text-kilted-green" />
              : <ImageIcon className="h-4 w-4 shrink-0 text-kilted-green" />}
            <button type="button" onClick={() => view(a)} data-testid={`attachment-open-${a.id}`}
              className="min-w-0 flex-1 truncate text-left font-medium text-kilted-green hover:underline">{a.filename}</button>
            <span className="shrink-0 text-xs text-muted-foreground">{(a.size / 1024 / 1024).toFixed(1)}MB · {a.uploaded_by}</span>
            {(user?.role === "admin" || a.uploader_id === user?.id) && (
              <button type="button" onClick={() => remove(a)} data-testid={`attachment-delete-${a.id}`}
                className="shrink-0 text-muted-foreground hover:text-red-600" title="Remove file"><X className="h-3.5 w-3.5" /></button>
            )}
          </div>
        ))}
      </div>
      <input ref={fileRef} type="file" accept={ATTACH_ACCEPT} className="hidden" onChange={upload} data-testid="attachment-file-input" />
      {atts.length < MAX_FILES && (
        <Button variant="outline" size="sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="attachment-add-button"
          className="mt-3 gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Attach a file
        </Button>
      )}
    </div>
  );
};

export default OrderAttachments;
